import { PlusIcon } from "@phosphor-icons/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDeleteDistrict } from "@/api/districts/mutations";
import { useDistrictsList } from "@/api/districts/queries";
import { QueryError } from "@/components/shared/query-error";
import { DataTablePagination } from "@/components/table/data-table-pagination";
import { useTableUrlState } from "@/components/table/use-table-url-state";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useDebounce } from "@/hooks/use-debounce";
import { CreateDistrictDMDialog } from "./create-district-dm-dialog";
import type { DeleteTarget } from "./district-actions";
import { DistrictActions } from "./district-actions";
import type { RenameTarget } from "./rename-district-dialog";
import { RenameDistrictDialog } from "./rename-district-dialog";

const COL_SPAN = 4;

function DividerRow() {
	return (
		<tr aria-hidden="true" className="pointer-events-none">
			<td colSpan={COL_SPAN} className="px-3 py-0">
				<div className="h-px bg-border/40" />
			</td>
		</tr>
	);
}

export function DistrictsTableDM() {
	const { t } = useTranslation();
	const { page, pageSize, search, setPage, setPageSize, setSearch } =
		useTableUrlState();
	const debouncedSearch = useDebounce(search);
	const [sheetOpen, setSheetOpen] = useState(false);
	const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
	const del = useDeleteDistrict();

	const { data, isLoading, isError, refetch } = useDistrictsList({
		page,
		page_size: pageSize,
		search: debouncedSearch || undefined,
	});

	const paginated = data?.results ?? [];
	const totalCount = data?.count ?? 0;
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

	function handleDelete() {
		if (!deleteTarget) return;
		del.mutate(deleteTarget.id, {
			onSuccess: () => {
				toast.success(t("districts.deleted"));
				setDeleteTarget(null);
			},
			onError: () => toast.error(t("districts.deleteError")),
		});
	}

	return (
		<>
			<CreateDistrictDMDialog
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
			/>
			<RenameDistrictDialog
				target={renameTarget}
				onOpenChange={(o) => !o && setRenameTarget(null)}
			/>
			<AlertDialog
				open={deleteTarget !== null}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("districts.deleteTitle")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("districts.deleteDescription", { name: deleteTarget?.name })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("districts.cancel")}</AlertDialogCancel>
						<AlertDialogAction onClick={handleDelete}>
							{t("districts.menuDelete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<div className="flex min-h-0 flex-1 flex-col gap-3">
				<div className="flex flex-wrap items-center gap-2">
					<SearchInput
						wrapperClassName="w-full sm:w-auto"
						className="w-full sm:w-72 md:w-96"
						placeholder={t("districts.searchPlaceholder")}
						value={search}
						onValueChange={setSearch}
					/>
					<Button className="ml-auto" onClick={() => setSheetOpen(true)}>
						<PlusIcon className="size-4" />
						{t("districts.createButton")}
					</Button>
				</div>

				<div className="min-h-0 flex-1 overflow-auto rounded-md border [&_[data-slot=table-container]]:overflow-x-visible">
					<Table>
						<TableHeader className="sticky top-0 z-10">
							<TableRow>
								<TableHead className="w-1/3">
									{t("districts.colName")}
								</TableHead>
								<TableHead className="w-1/3">
									{t("districts.colApartments")}
								</TableHead>
								<TableHead className="w-1/3">
									{t("districts.colMeters")}
								</TableHead>
								<TableHead className="w-14" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{isLoading ? (
								<>
									{Array.from({ length: 3 }).map((_, i) => (
										<React.Fragment key={i}>
											{i > 0 && <DividerRow />}
											<TableRow>
												<TableCell>
													<Skeleton className="h-4 w-40" />
												</TableCell>
												<TableCell>
													<Skeleton className="h-4 w-10" />
												</TableCell>
												<TableCell>
													<Skeleton className="h-4 w-10" />
												</TableCell>
												<TableCell className="w-14" />
											</TableRow>
										</React.Fragment>
									))}
								</>
							) : isError ? (
								<TableRow>
									<TableCell colSpan={COL_SPAN} className="py-8">
										<QueryError onRetry={() => refetch()} />
									</TableCell>
								</TableRow>
							) : paginated.length > 0 ? (
								<>
									{paginated.map((district, i) => (
										<React.Fragment key={district.id}>
											{i > 0 && <DividerRow />}
											<TableRow>
												<TableCell className="font-medium">
													{district.name}
												</TableCell>
												<TableCell className="text-muted-foreground">
													{district.apartments_count}
												</TableCell>
												<TableCell className="text-muted-foreground">
													{district.meters_count}
												</TableCell>
												<TableCell
													className="w-14"
													onClick={(e) => e.stopPropagation()}
												>
													<div className="flex justify-end">
														<DistrictActions
															district={district}
															onRename={setRenameTarget}
															onDelete={setDeleteTarget}
														/>
													</div>
												</TableCell>
											</TableRow>
										</React.Fragment>
									))}
								</>
							) : (
								<TableRow>
									<TableCell
										colSpan={COL_SPAN}
										className="py-8 text-center text-muted-foreground"
									>
										{search
											? t("districts.noResults")
											: t("districts.noDistricts")}
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>

				<DataTablePagination
					page={page}
					pageSize={pageSize}
					count={totalCount}
					onPageChange={setPage}
					onPageSizeChange={(size) => setPageSize(size)}
					hasPrevious={page > 1}
					hasNext={page < totalPages}
				/>
			</div>
		</>
	);
}
